import { createContext, useCallback, useContext, useEffect, useRef } from 'react'
import type {
  HotelSearchResultsData,
  ExperienceSearchResultsData,
  HotelSearchErrorData,
  ExperienceSearchErrorData,
  ChatUiActionData,
  HotelMapViewData,
  ExperienceMapViewData,
  OfferMapSuggestionData,
  PlaceMapSuggestionData,
} from 'tripPlanner/api/types'
import type { LocationInfoData } from 'tripPlanner/components/Chat/Message'
import type { ChatMapOnMapData, CruiseTransportMapPoint, LEExploreCategorySource } from 'tripPlanner/assistant/mapTypes'
import type { OfferClickSearchParams } from './ChatMessageActionsContext'
import type { TripPlannerSurfaceActivityEntry } from 'tripPlanner/assistant/surfaceSnapshots/surfaceActivityEntryTypes'
import type { TripPlannerCommittedStateEntry } from 'tripPlanner/assistant/surfaceSnapshots/committedStateEntry'

export type { CruiseTransportMapPoint, ChatMapOnMapData, LEExploreCategorySource } from 'tripPlanner/assistant/mapTypes'

// ── Event Types ──────────────────────────────────────────────────────
export type TripPlannerEvent =
  // Stream events (dispatched by TripPlannerChatProvider)
  | { type: 'hotel_search_results'; data: HotelSearchResultsData; generation: number }
  | { type: 'experience_search_results'; data: ExperienceSearchResultsData; generation: number }
  | { type: 'hotel_search_error'; data: HotelSearchErrorData; generation: number }
  | { type: 'experience_search_error'; data: ExperienceSearchErrorData; generation: number }
  | { type: 'ui_action'; data: ChatUiActionData }
  // Navigation (dispatched by orchestrator)
  | { type: 'reset_chat_state' }
  // Cross-container actions
  | { type: 'show_hotel_on_map'; data: HotelMapViewData }
  | { type: 'show_experience_on_map'; data: ExperienceMapViewData }
  | { type: 'show_offer_suggestion_on_map'; data: OfferMapSuggestionData }
  | { type: 'show_place_on_map'; data: PlaceMapSuggestionData }
  | { type: 'show_chat_map_on_map'; data: ChatMapOnMapData }
  | { type: 'show_cruise_transport_on_map'; points: Array<CruiseTransportMapPoint> }
  | { type: 'show_explore_on_map'; coordinates: { lat: number; lng: number }; source: LEExploreCategorySource }
  | { type: 'ask_ai_for_day'; dayNumber: number; title: string; date?: string }
  | { type: 'ask_ai_for_item'; name: string; category?: string; itemId?: number; description?: string; locationRefinementPrompt?: string; action?: 'add_location' | 'choose_location' }
  | { type: 'ask_ai_for_list_row'; title: string; context: string; placeholder?: string; origin?: { x: number; y: number } }
  | { type: 'activate_drawer_ask_ai_context'; source: string; label: string; context: string; placeholder?: string }
  | { type: 'clear_drawer_ask_ai_context'; source: string }
  | { type: 'open_place_drawer'; googlePlaceId: string; placeName: string }
  | { type: 'open_place_search_drawer'; searchQuery: string; placeName: string }
  | { type: 'open_location_drawer'; data: LocationInfoData }
  | { type: 'open_detail_drawer'; googlePlaceId: string; placeName: string; countryName?: string; stopId?: number }
  | { type: 'open_offer_drawer'; offerId: string; accommodationPropertyId?: string; searchParams?: OfferClickSearchParams; offerType?: string; scrollToPricing?: boolean; scrollToRoomOptions?: boolean }
  | { type: 'open_listing_search_drawer'; vertical: 'hotel' | 'experience'; placeName: string; placeCountry?: string; placeId?: string }
  | { type: 'right_panel_domain_drawer_state_changed'; isOpen: boolean }
  | { type: 'close_right_panel_domain_drawers' }
  | { type: 'open_cart_panel' }
  | { type: 'itinerary_changed' }
  // Dispatched by the "Added to trip" snackbar's View action. Surfaces that own a view
  // switcher bring the Timeline forward; the itinerary state re-targets the scroll.
  | { type: 'view_itinerary_item'; stopId: number; dayId?: number; itemId?: number }
  // Dispatched once an add actually lands. Drawers stay open while the add is in flight so
  // the "Adding to trip" snackbar is visible over them, then close on this.
  | { type: 'itinerary_item_added' }
  // No buffer/replay on this bus: dispatched before useSurfaceActivityAccumulator mounts is lost
  // silently, with no TTL to recover it later.
  | { type: 'trip_planner_surface_activity'; entries: Array<TripPlannerSurfaceActivityEntry> }
  // Same no-buffer/no-replay caveat as trip_planner_surface_activity above, but a genuinely
  // different loss: a committed-state entry dispatched before useCommittedStateAccumulator mounts
  // is a currently-true fact silently missing from the model's context for its whole TTL, not just
  // one turn's activity log.
  | { type: 'trip_planner_committed_state'; entries: Array<TripPlannerCommittedStateEntry> }
  // Dispatched when a non-chat surface sends a message to the assistant, so surfaces
  // that hide the conversation (mobile tabs) can bring the chat back into view.
  | { type: 'focus_chat' }
  /** Focus the mobile floating Ask AI input on Timeline/List or an open drawer footer. */
  | { type: 'focus_floating_ask_ai' }

type EventHandler = (event: TripPlannerEvent) => void

interface TripPlannerEventBusValue {
  dispatch: (event: TripPlannerEvent) => void
  subscribe: (handler: EventHandler) => () => void
}

const TripPlannerEventBusContext = createContext<TripPlannerEventBusValue | undefined>(undefined)

export function TripPlannerEventBusProvider({ children }: { children: React.ReactNode }) {
  const subscribersRef = useRef(new Set<EventHandler>())

  const dispatch = useCallback((event: TripPlannerEvent) => {
    subscribersRef.current.forEach(handler => {
      try {
        handler(event)
      } catch(error) {
        console.error('[TripPlannerEventBus] Subscriber error:', error)
      }
    })
  }, [])

  const subscribe = useCallback((handler: EventHandler) => {
    subscribersRef.current.add(handler)
    return () => { subscribersRef.current.delete(handler) }
  }, [])

  // Value is stable — dispatch and subscribe never change
  const value = useRef({ dispatch, subscribe }).current

  return (
    <TripPlannerEventBusContext.Provider value={value}>
      {children}
    </TripPlannerEventBusContext.Provider>
  )
}

export function useTripPlannerEventBus() {
  const ctx = useContext(TripPlannerEventBusContext)
  if (!ctx) throw new Error('useTripPlannerEventBus must be within TripPlannerEventBusProvider')
  return ctx
}

/**
 * Like useTripPlannerEventBus but returns undefined instead of throwing when
 * there is no provider. Use in components that may render outside the chat
 * runtime (e.g. in isolated tests) and can degrade gracefully without the bus.
 */
export function useTripPlannerEventBusOptional() {
  return useContext(TripPlannerEventBusContext)
}

function matchesEventType<T extends TripPlannerEvent['type']>(
  event: TripPlannerEvent,
  eventType: T | Array<T>,
): event is Extract<TripPlannerEvent, { type: T }> {
  if (Array.isArray(eventType)) {
    return eventType.some(type => event.type === type)
  }
  return event.type === eventType
}

/** Subscribe to specific event type(s). Handler is called only for matching events. */
export function useEventSubscription<T extends TripPlannerEvent['type']>(
  eventType: T | Array<T>,
  handler: (event: Extract<TripPlannerEvent, { type: T }>) => void,
) {
  const { subscribe } = useTripPlannerEventBus()
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  const stableTypes = useRef(eventType)
  stableTypes.current = eventType

  useEffect(() => {
    return subscribe((event) => {
      if (matchesEventType(event, stableTypes.current)) {
        handlerRef.current(event)
      }
    })
  }, [subscribe])
}
